import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import { OperatingHoursRequest } from '../../types';

const DAY_KEYS = [
  'onboarding.hours.sunday',
  'onboarding.hours.monday',
  'onboarding.hours.tuesday',
  'onboarding.hours.wednesday',
  'onboarding.hours.thursday',
  'onboarding.hours.friday',
  'onboarding.hours.saturday',
];

function jsToIsoDow(jsDow: number): number {
  return jsDow === 0 ? 7 : jsDow;
}

function isoToJsDow(isoDow: number): number {
  return isoDow === 7 ? 0 : isoDow;
}

const DEFAULT_HOURS: OperatingHoursRequest[] = [0, 1, 2, 3, 4, 5, 6].map((d) => ({
  dayOfWeek: d,
  openTime: '09:00',
  closeTime: '21:00',
  isClosed: d === 0,
}));

export default function WorkingHoursSettingsTab() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [hours, setHours] = useState<OperatingHoursRequest[]>(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api.getMyGarage(token)
      .then((data) => {
        const fetched = (data.operatingHours || []).map((h) => ({
          dayOfWeek: isoToJsDow(h.dayOfWeek),
          openTime: h.openTime || '09:00',
          closeTime: h.closeTime || '21:00',
          isClosed: h.isClosed,
        }));
        const merged = DEFAULT_HOURS.map((d) => fetched.find((f) => f.dayOfWeek === d.dayOfWeek) || d);
        setHours(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const updateDay = (dayOfWeek: number, updates: Partial<OperatingHoursRequest>) => {
    setHours((prev) => prev.map((h) => (h.dayOfWeek === dayOfWeek ? { ...h, ...updates } : h)));
    setSavedAt(null);
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const payload = hours.map((h) => ({
        dayOfWeek: jsToIsoDow(h.dayOfWeek),
        openTime: h.openTime,
        closeTime: h.closeTime,
        isClosed: h.isClosed,
      }));
      await api.setOperatingHours(payload, token);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : (t('common.saveFailed') || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-tab-content"><div>{t('common.loading') || 'Loading…'}</div></div>;
  }

  return (
    <div className="settings-tab-content">
      <div className="settings-section">
        <h3 className="settings-section-title">{t('settings.workingHours.title') || 'Working Hours'}</h3>

        <table className="hours-table">
          <thead>
            <tr>
              <th>{t('onboarding.hours.day') || 'Day'}</th>
              <th>{t('onboarding.hours.open') || 'Open'}</th>
              <th>{t('onboarding.hours.openTime') || 'Open time'}</th>
              <th>{t('onboarding.hours.closeTime') || 'Close time'}</th>
            </tr>
          </thead>
          <tbody>
            {hours.map((h) => (
              <tr key={h.dayOfWeek}>
                <td>{t(DAY_KEYS[h.dayOfWeek])}</td>
                <td>
                  <label className="settings-day-toggle">
                    <input
                      type="checkbox"
                      checked={!h.isClosed}
                      onChange={(e) => updateDay(h.dayOfWeek, { isClosed: !e.target.checked })}
                    />
                    <span className="settings-day-toggle-slider" />
                  </label>
                </td>
                <td>
                  <input
                    type="time"
                    value={h.openTime}
                    disabled={h.isClosed}
                    onChange={(e) => updateDay(h.dayOfWeek, { openTime: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={h.closeTime}
                    disabled={h.isClosed}
                    onChange={(e) => updateDay(h.dayOfWeek, { closeTime: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && <div className="error-message" style={{ marginTop: '0.75rem' }}>{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
          <button className="settings-btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? (t('common.saving') || 'Saving…') : (t('common.save') || 'Save')}
          </button>
          {savedAt && !saving && !error && (
            <span style={{ color: '#15803d', fontSize: '13px', fontWeight: 500 }}>
              {t('settings.workingHours.saved') || 'Saved ✓'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
